/**
 * A plan choice made before signing in.
 *
 * The pricing cards on the landing page are pickable, but checkout lives behind
 * auth, and the signup redirect throws away in-memory state. So the choice is
 * stashed here on Proceed and rehydrated by the checkout flow once the visitor
 * is back, which is what lets someone pick their features before they have an
 * account.
 *
 * Deliberately short-lived: a selection from a previous visit is more likely to
 * confuse than to help, so it expires rather than silently pre-ticking a stale
 * set of checks weeks later.
 */

const KEY = 'yourchecka.planSelection';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface StashedSelection {
    checkType: string;
    /** Feature ids the visitor kept. Empty means they cleared everything. */
    features: string[];
    addons: string[];
    /**
     * Plan name and the charge for one person under this selection. Carried so
     * checkout can skip the plan picker altogether — someone who already chose
     * on the pricing page should not be asked to choose again. Optional because
     * a stash written by an older build will not have them.
     */
    planName?: string;
    pricePerSubject?: number;
    savedAt: number;
}

export function stashSelection(selection: Omit<StashedSelection, 'savedAt'>): void {
    try {
        localStorage.setItem(KEY, JSON.stringify({ ...selection, savedAt: Date.now() }));
    } catch {
        // Private browsing, disabled storage, quota. The visitor simply picks
        // again at checkout, so this must never break the Proceed button.
    }
}

/** The stashed choice, or null if there is none, it is stale, or unreadable. */
export function readSelection(): StashedSelection | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as StashedSelection;
        if (typeof parsed?.checkType !== 'string' || !Array.isArray(parsed.features)) {
            return null;
        }
        if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
            clearSelection();
            return null;
        }
        return { ...parsed, addons: Array.isArray(parsed.addons) ? parsed.addons : [] };
    } catch {
        return null;
    }
}

export function clearSelection(): void {
    try {
        localStorage.removeItem(KEY);
    } catch {
        // Nothing to do; a stale entry expires on its own.
    }
}
